/**
 * Utility functions for formatting strategy names in notifications and toasts
 * with responsive truncation for space-constrained displays
 */

// Unified strategy truncation constants for consistency across components
export const STRATEGY_TRUNCATION = {
  mobile: 10,    // Standardized mobile length
  desktop: 16,   // Standardized desktop length
  panel: {
    mobile: 10,   // Panel mobile same as toast
    desktop: 16   // Panel desktop same as toast
  }
} as const;

/**
 * Truncates strategy name based on device/context constraints
 * @param strategy - The strategy name to truncate
 * @param isMobile - Whether this is for mobile display (default: auto-detect)
 * @param maxLength - Override default max length
 * @returns Truncated strategy name or empty string if no strategy
 */
export const truncateStrategy = (
  strategy: string | undefined | null, 
  isMobile?: boolean, 
  maxLength?: number
): string => {
  if (!strategy) return '';
  
  // Auto-detect mobile if not specified
  const isSmallScreen = isMobile ?? (typeof window !== 'undefined' && window.innerWidth < 768);
  
  // Set default max lengths using standardized constants
  const defaultMaxLength = maxLength ?? (isSmallScreen ? STRATEGY_TRUNCATION.mobile : STRATEGY_TRUNCATION.desktop);
  
  return strategy.length > defaultMaxLength 
    ? `${strategy.substring(0, defaultMaxLength - 3)}...` 
    : strategy;
};

/**
 * Formats strategy name for display in parentheses
 * @param strategy - The strategy name
 * @param isMobile - Whether this is for mobile display
 * @returns Formatted strategy string with parentheses or empty string
 */
export const formatStrategyDisplay = (
  strategy: string | undefined | null, 
  isMobile?: boolean
): string => {
  const truncated = truncateStrategy(strategy, isMobile);
  return truncated ? ` (${truncated})` : '';
};

/**
 * Creates enhanced toast message with strategy information
 * @param symbol - Trading symbol (e.g., "BTCUSDT")
 * @param action - Action description (e.g., "position opened", "closed +$123.45")
 * @param strategy - Strategy name
 * @param isMobile - Whether this is for mobile display
 * @returns Formatted message string
 */
export const createEnhancedToastMessage = (
  symbol: string,
  action: string,
  strategy?: string | null,
  isMobile?: boolean
): string => {
  const strategyDisplay = formatStrategyDisplay(strategy, isMobile);
  
  // For closing events, put strategy after symbol but before action
  if (action.includes('closed') || action.includes('PnL:')) {
    return `${symbol}${strategyDisplay} ${action}`;
  }
  
  // For opening events, put strategy at the end
  return `${action}${strategyDisplay}`;
};

/**
 * Creates enhanced milestone toast message with account and strategy information
 * @param symbol - Trading symbol (e.g., "BTCUSDT")
 * @param action - Action description (e.g., "📈 25% Profit (+$123.45)")
 * @param account - Account name
 * @param strategy - Strategy name
 * @param isMobile - Whether this is for mobile display
 * @returns Formatted message string
 */
export const createMilestoneToastMessage = (
  symbol: string,
  action: string,
  account?: string | null,
  strategy?: string | null,
  isMobile?: boolean
): string => {
  const strategyDisplay = formatStrategyDisplay(strategy, isMobile);
  
  // Format account display (truncate if too long)
  let accountDisplay = '';
  if (account) {
    const isSmallScreen = isMobile ?? (typeof window !== 'undefined' && window.innerWidth < 768);
    const maxAccountLength = isSmallScreen ? 8 : 12;
    const truncatedAccount = account.length > maxAccountLength 
      ? `${account.substring(0, maxAccountLength - 3)}...` 
      : account;
    accountDisplay = ` [${truncatedAccount}]`;
  }
  
  // Format: SYMBOL [ACCOUNT] ACTION (STRATEGY)
  return `${symbol}${accountDisplay} ${action}${strategyDisplay}`;
};

/**
 * Creates mobile-optimized milestone message with better readability
 * @param symbol - Trading symbol 
 * @param directionIcon - 📈 or 📉
 * @param milestoneText - "25% Profit"
 * @param currentPnl - Current P&L value
 * @param strategy - Strategy name
 * @returns Multi-line mobile-formatted message
 */
export const createMobileMilestoneMessage = (
  symbol: string,
  directionIcon: string,
  milestoneText: string,
  currentPnl?: number,
  strategy?: string | null
): string => {
  const pnlText = currentPnl ? ` $${currentPnl > 0 ? '+' : ''}${currentPnl.toFixed(2)}` : '';
  const strategyDisplay = truncateStrategy(strategy, true, 8); // Shorter for mobile
  
  // Primary line: Most critical info
  const primaryLine = `${directionIcon} ${milestoneText}${pnlText}`;
  
  // Secondary line: Context info
  const secondaryLine = strategyDisplay 
    ? `${symbol} • ${strategyDisplay}`
    : symbol;
  
  return `${primaryLine}\n${secondaryLine}`;
};

/**
 * Creates metadata line for notification panel with strategy, account, and timestamp
 * @param strategy - Strategy name
 * @param account - Account name  
 * @param timestamp - Notification timestamp
 * @param isMobile - Whether this is for mobile display
 * @returns Formatted metadata string
 */
export const createNotificationMetadata = (
  strategy: string | undefined | null,
  account: string | undefined | null,
  timestamp: number,
  isMobile?: boolean
): string => {
  const parts: string[] = [];
  
  // Add truncated strategy if available
  const truncatedStrategy = truncateStrategy(strategy, isMobile);
  if (truncatedStrategy) {
    parts.push(truncatedStrategy);
  }
  
  // Add account if available and different from strategy
  if (account && account !== strategy) {
    parts.push(account);
  }
  
  // Add timestamp
  parts.push(formatTimestamp(timestamp));
  
  return parts.join(' • ');
};

/**
 * Enhanced timestamp formatting with event vs notification distinction
 */
export const formatEventTimestamp = (
  eventTimestamp?: number, 
  notificationTimestamp?: number,
  isRecoveryEvent?: boolean
): string => {
  // Use event timestamp if available, otherwise fall back to notification timestamp
  const timestamp = eventTimestamp || notificationTimestamp || Date.now();
  const now = Date.now();
  const diff = now - timestamp;
  
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  // For recovery events, show the actual event time with indication
  if (isRecoveryEvent && eventTimestamp) {
    let timeStr = '';
    if (minutes < 1) timeStr = 'Just now';
    else if (minutes < 60) timeStr = `${minutes}m ago`;
    else if (hours < 24) timeStr = `${hours}h ago`;
    else timeStr = `${days}d ago`;
    
    return timeStr;
  }
  
  // For real-time events, use standard formatting
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

/**
 * Formats timestamp for notification display
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Human-readable relative time string
 */
export const formatTimestamp = (timestamp: number): string => {
  return formatEventTimestamp(undefined, timestamp, false);
};

/**
 * Hook to detect if current device is mobile-sized
 * @returns boolean indicating if screen is mobile-sized
 */
export const useIsMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Re-export React for the hook
import React from 'react';